import type {
  QueuedReason,
  SpendLimitScope,
  TaskLabelView,
  TaskView,
  UserSummary,
  WorkflowStepType,
  WorkflowView,
} from '@/lib/api/generated/model';

import { buildBoard } from './boardModel';
import { stepEntryTime, taskCost, type TaskCost } from './executionHistory';
import { deriveCardExecutionState, latestExecution } from './executionStatus';
import { locateTask } from './locateTask';
import { notStartedCount } from './linkedChildren';
import type { AttentionKind } from './workspaceSummary';

/** One task in the workspace, flattened out of the board that owns it. */
export interface WorkspaceTaskRow {
  taskId: string;
  title: string;
  description: string | null;

  /** For the link. A page spanning workflows cannot locate a task by id alone. */
  workflowKey: string;
  workflowName: string;

  /** `null` only for an orphan — a task on a step its workflow no longer declares. */
  stepLabel: string | null;
  /**
   * Whether an agent or a person holds that step — the difference between a task
   * that will move on its own and one that will not.
   *
   * Overview's rows already carry this and render it on the same
   * "workflow · step" line. Omitting it here made the identical line say less on
   * the page whose whole purpose is finding the task that needs you.
   */
  stepKind: WorkflowStepType | null;
  /** True when that position names no step on the board; the row marks it out of place. */
  orphaned: boolean;

  state: 'running' | 'waiting' | 'failed' | 'at-rest';
  /** Why, when `state` is `failed`. `null` otherwise — there is no question to answer. */
  kind: AttentionKind | null;

  labels: readonly TaskLabelView[];
  /** How many comments the task carries; zero renders nothing on the row. */
  commentCount: number;
  updatedAt: string;

  /** False for a task on a draft workflow — the row renders unlinked. */
  hasBoard: boolean;

  /*
    What the Tasks page shows beyond the state word (spec 110).

    Every member below is a **display** fact. None of them moves a task between
    the four `state` buckets, because `state` is what the shared state filter
    matches — on this page and on a board — and a blocked or finished task that
    changed bucket here would filter differently from the same task on its board
    (068 FR-009). A blocked task still sits in `at-rest`; the row just says
    "Spend limit" instead.
  */
  createdAt: string;
  createdBy: UserSummary | null;
  assignee: UserSummary | null;
  needsReview: boolean;
  needsAttention: boolean;
  /**
   * The latest run was stopped by a person. Read here, as the Overview reads
   * it, so the two pages agree on what needs a person without depending on
   * the service flag alone (spec 152 FR-015).
   */
  stopped: boolean;
  /** Created tasks waiting for a person to start them (spec 152). */
  toStart: number;
  /**
   * The task's run waits on the subtasks it created (an open join), so its
   * review and attention flags are the service's roll-up of those subtasks
   * (spec 101 FR-009), not needs of its own. The subtasks carry those; counting the parent too would
   * count one need twice (spec 152 FR-015).
   */
  rolledUp: boolean;
  /**
   * The task's run waits on the subtasks it created: `state` reads `running`,
   * but it holds no run slot (spec 150 FR-003). Decided where the Overview's
   * summary decides it, so the Tasks page's "Waiting on subtasks" lists the
   * tasks the Overview counts (spec 153 FR-018).
   */
  waitingOnSubtasks: boolean;
  /** How far along the tasks this one created are; `null` when it created none. */
  linkedTasks: { done: number; total: number } | null;
  /**
   * Which spend limit refused the latest run, or `null` when none did.
   * `'unknown'` for a run blocked before the service recorded which limit.
   */
  blocked: SpendLimitScope | 'unknown' | 'account' | null;
  /**
   * Waiting for a slot (spec 115): its place in line and why, or `null` when the
   * latest run is not queued. A display fact like `blocked`: the task stays
   * `at-rest` for matching, because nothing is running yet and the board's
   * "running" filter must not match it.
   */
  queued: { position: number | null; reason: QueuedReason | null } | null;
  /** Standing on the workflow's done column. */
  finished: boolean;
  /**
   * When it reached the done column — the synthetic run recorded there, the
   * same moment a board card reads as "time on this step". `null` unless
   * `finished`.
   */
  finishedAt: string | null;
  /** The same figure the board card shows (spec 089), so the two never disagree. */
  cost: TaskCost;
  /**
   * Hidden from boards and, by default, from the Tasks page. Only present in
   * a read that asked for hidden tasks (spec 112).
   */
  hidden: boolean;
}

/**
 * Every task in the workspace, as flat rows, with each task's state derived the
 * way a board card derives it.
 *
 * **The derivation is reused, not reimplemented.** A task's state comes from
 * building its workflow's board, locating the task on it, and reading
 * `deriveCardExecutionState` — the same path `workspaceSummary.ts` takes and the
 * same one a card takes. `features/tasks/index.ts` carries a comment refusing to
 * export `buildBoard`, `locateTask` and `deriveCardExecutionState` for exactly
 * this reason: one finished answer crosses the package boundary instead of the
 * parts needed to compute it again. This function is that finished answer for a
 * second caller, so those three stay internal.
 *
 * **The order of refusals matters and is copied deliberately.** An orphaned
 * position and a failed workdir are read *before* the card state, because both
 * mean the task cannot proceed at all, where every card state describes a task
 * that is somewhere legitimate. `locateTask` returns `undefined` for an orphan
 * by design, so reading the card state first would describe a stranded task as
 * merely at rest. Change this order and this page starts disagreeing with
 * Overview about the same task.
 *
 * Ordering of the rows themselves is not done here: it belongs to the surface
 * that displays them, and this function has no opinion about it.
 *
 * **On the duplicated traversal, since a review raised it.** This builds every
 * workflow's board, and so does `summarizeWorkspaceTasks` — two walks over the
 * same cached aggregate. Two things bound the cost, and neither is an argument
 * that the duplication is elegant:
 *
 * - The two are on **different routes**, never mounted together, so only one
 *   runs on any given page. The predicted "twice per poll tick" would need both
 *   pages open at once, which the router does not allow.
 * - TanStack Query's structural sharing is on by default, so a refetch that
 *   changes nothing returns the same object references and the `useMemo`
 *   guarding this call does not re-run. An idle poll costs nothing here; only a
 *   poll that actually changes data re-derives.
 *
 * What remains true is that a real change re-walks every board, which is the
 * cost `066` already accepted knowingly for the same reason: the alternative is
 * a second, lighter reading of "what state is this task in", and this codebase
 * has four comments explaining why there must not be one.
 */
export function listWorkspaceTasks(
  workflows: WorkflowView[],
  tasks: TaskView[],
): WorkspaceTaskRow[] {
  const rows: WorkspaceTaskRow[] = [];

  for (const workflow of workflows) {
    const held = tasks.filter((task) => task.workflow_id === workflow.id);
    if (held.length === 0) continue;

    const result = buildBoard(workflow, held);

    // A workflow whose columns cannot be derived says nothing about its tasks.
    // Overview reports the workflow and abandons them rather than reading them
    // against a board that does not exist; this page drops them for the same
    // reason — a row claiming a state it could not compute is worse than a row
    // that is not there.
    if (!result.ok) continue;

    const { board } = result;
    // A draft has no committed definition and therefore no board, so a row for
    // one of its tasks can link nowhere.
    const hasBoard = workflow.status === 'published';

    for (const task of held) {
      const location = locateTask(board, task.id);
      const orphaned = board.orphanTaskIds.has(task.id);

      const finished = !orphaned && location?.step.systemRole === 'done';
      const linked = task.linked_tasks;

      const base = {
        taskId: task.id,
        title: task.title,
        description: task.description ?? null,
        workflowKey: workflow.key,
        workflowName: workflow.name,
        stepLabel: location?.stepLabel ?? null,
        stepKind: location?.step.kind ?? null,
        orphaned,
        labels: task.labels ?? [],
        commentCount: task.comment_count ?? 0,
        updatedAt: task.updated_at,
        hasBoard,
        createdAt: task.created_at,
        createdBy: task.created_by ?? null,
        assignee: task.assignee ?? null,
        needsReview: task.needs_review ?? false,
        needsAttention: task.needs_attention ?? false,
        stopped: false,
        toStart: notStartedCount(linked),
        rolledUp:
          linked != null &&
          latestExecution(task.executions ?? [])?.status ===
            'awaiting_children',
        waitingOnSubtasks: false,
        linkedTasks:
          linked && linked.total > 0
            ? { done: linked.done, total: linked.total }
            : null,
        blocked: null,
        queued: null,
        finished,
        finishedAt: finished ? stepEntryTime(task) : null,
        cost: taskCost(task),
        hidden: task.hidden_at != null,
      };

      if (orphaned) {
        rows.push({ ...base, state: 'failed', kind: 'orphaned' });
        continue;
      }

      if (task.workdir?.status === 'failed') {
        rows.push({ ...base, state: 'failed', kind: 'failed-workdir' });
        continue;
      }

      const derived = deriveCardExecutionState(task, location?.step);

      switch (derived.kind) {
        case 'live':
          rows.push({
            ...base,
            state: 'running',
            kind: null,
            waitingOnSubtasks: derived.children !== undefined,
          });
          break;
        case 'waiting-human':
          rows.push({ ...base, state: 'waiting', kind: null });
          break;
        case 'failed':
          rows.push({ ...base, state: 'failed', kind: 'failed-run' });
          break;
        case 'blocked':
          // At rest for matching, like every state nothing on this page can
          // act on — the board's "waiting" filter excludes a blocked task too.
          // Only what the row *says* differs, so the scope rides alongside.
          rows.push({
            ...base,
            state: 'at-rest',
            kind: null,
            blocked: derived.scope ?? 'unknown',
          });
          break;
        case 'needs-account':
          // Blocked too, for the blocked filter; its words are its own (spec 160):
          // its person links an account, nobody raises a limit.
          rows.push({
            ...base,
            state: 'at-rest',
            kind: null,
            blocked: 'account',
          });
          break;
        case 'queued':
          rows.push({
            ...base,
            state: 'at-rest',
            kind: null,
            queued: { position: derived.position, reason: derived.reason },
          });
          break;
        default:
          // `not-started`, `completed`, `stopped` and `idle` — the states
          // nothing is blocked on. `stopped` belongs here rather than beside
          // `failed`: a run someone ended on purpose leaves the task at rest,
          // not broken. The same collapse `workspaceSummary.ts` makes in its
          // own default branch, for the same reason.
          rows.push({
            ...base,
            state: 'at-rest',
            kind: null,
            stopped: derived.kind === 'stopped',
          });
          break;
      }
    }
  }

  return rows;
}
